import { Avatar, Paper, Divider } from "@mui/material";
import Text from "../../../../../components/Text";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Flex from "../../../../../components/Flex";
import ViewFileModal from "../../../../../components/ViewFileModal";
import SubmitFaxPopUp from "./HeaderActions/SubmitFaxPopUp";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import SendToPatientModal from "./HeaderActions/SendToPatientModal";
import AssignAndCreateTaskModal from "./HeaderActions/AssignAndCreateTaskModal";
import { formatDateTime } from "../../../../../utils/date-time-utility";

interface FaxHeaderProps {
  faxData?: {
    pages?: string;
    size?: string;
    result?: string;
    epochTime?: number;
    callerId?: string;
    date?: string;
    toFaxNumber?: string | null;
    dateSent?: string | null;
    sFaxFileName?: string;
    [key: string]: any;
  } | null;
  isIncomingFax?: boolean;
}

const FaxHeader = ({ faxData, isIncomingFax }: FaxHeaderProps) => {
  const [
    viewDisclosure,
    addToPatientChartDisclosure,
    assignAndCreateTaskDisclosure,
  ] = useReduceDisclosure(3);

  if (!faxData) {
    return null;
  }
  // Extract name from sFaxFileName or callerId
  const displayName = faxData.callerId ? `+${faxData.callerId}` : "Fax";

  // Get phone number - prefer toFaxNumber, fallback to callerId
  const phoneNumber =
    faxData.toFaxNumber || (faxData.callerId ? `+${faxData.callerId}` : "-");

  // Format size (bytes to readable format)
  const formatSize = (size: string | undefined) => {
    if (!size) return "-";
    const bytes = Number.parseInt(size, 10);
    if (Number.isNaN(bytes)) return size;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formattedSize = formatSize(faxData.size);

  // Convert base64 result to data URL for preview
  const getFileUrl = () => {
    if (!faxData.result) return null;
    // Check if result is already a data URL
    if (faxData.result.startsWith("data:")) {
      return faxData.result;
    }
    // If it's base64, convert to data URL (assuming PDF for fax)
    return `data:application/pdf;base64,${faxData.result}`;
  };

  const fileUrl = getFileUrl();

  return (
    <Flex flexDirection="column" gap={2} width="100%">
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 1.5,
          width: "100%",
          border: "1px solid #e0e0e0",
        }}
      >
        <Flex
          gap={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
          width="100%"
        >
          <Flex gap={1.5} alignItems="flex-start" flex={1}>
            <Avatar
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: 40,
                height: 40,
              }}
            >
              <PersonIcon />
            </Avatar>
            <Flex flexDirection="column" gap={1} flex={1}>
              <Text fontWeight={600} fontSize="16px" color="text.primary">
                {displayName}
              </Text>

              <Flex gap={1.5} alignItems="center" flexWrap="wrap">
                <Text fontSize="14px" color="text.secondary">
                  To :{" "}
                  <Text component="span" fontWeight={600} color="text.primary">
                    {phoneNumber}
                  </Text>
                </Text>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: "16px",
                    alignSelf: "center",
                    borderColor: "rgba(0, 0, 0, 0.12)",
                  }}
                />

                {faxData.date && (
                  <Text fontSize="14px" color="text.secondary">
                    Date :{" "}
                    <Text
                      component="span"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {faxData.date ? formatDateTime(faxData.date) : ""}
                    </Text>
                  </Text>
                )}
                {faxData.dateSent && (
                  <Text fontSize="14px" color="text.secondary">
                    Date :{" "}
                    <Text
                      component="span"
                      fontWeight={600}
                      color="text.primary"
                    >
                      {faxData.dateSent ? formatDateTime(faxData.dateSent) : ""}
                    </Text>
                  </Text>
                )}

                {faxData.pages && (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: "16px",
                        alignSelf: "center",
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />

                    <Text fontSize="14px" color="text.secondary">
                      Pages :{" "}
                      <Text
                        component="span"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {faxData.pages}
                      </Text>
                    </Text>
                  </>
                )}

                {formattedSize && formattedSize !== "-" && (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: "16px",
                        alignSelf: "center",
                        borderColor: "rgba(0, 0, 0, 0.12)",
                      }}
                    />

                    <Text fontSize="14px" color="text.secondary">
                      Size :{" "}
                      <Text
                        component="span"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {formattedSize}
                      </Text>
                    </Text>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
          {isIncomingFax && (
            <SubmitFaxPopUp
              onClick={(v) => {
                if (v === "add_to_patient_chart") {
                  addToPatientChartDisclosure.open(faxData);
                } else if (v === "assign_to_patient") {
                  assignAndCreateTaskDisclosure.open(faxData);
                }
              }}
            />
          )}
        </Flex>
      </Paper>

      {/* Document Preview Section */}
      {fileUrl && (
        <Flex flexDirection="column" gap={1}>
          <Text fontSize="14px" fontWeight={500}>
            Documents
          </Text>
          <Flex gap={2} flexWrap="wrap">
            <Paper
              elevation={2}
              onClick={() => viewDisclosure.open(fileUrl)}
              sx={{
                width: 200,
                height: 250,
                p: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                "&:hover": {
                  elevation: 4,
                },
              }}
            >
              <Flex
                alignItems="center"
                gap={0.5}
                sx={{ mb: 1, width: "100%", minWidth: 0 }}
              >
                <VisibilityIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    flex: 1,
                  }}
                  title={faxData.sFaxFileName || "Fax Document"}
                >
                  {faxData.sFaxFileName || "Fax Document"}
                </Text>
              </Flex>
              <Flex
                flex={1}
                width="100%"
                alignItems="center"
                justifyContent="center"
                onClick={() => viewDisclosure.open(fileUrl)}
                sx={{
                  backgroundColor: "grey.100",
                  borderRadius: 1,
                  cursor: "pointer",
                }}
              >
                <iframe
                  src={fileUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  title="Fax document preview"
                />
              </Flex>
            </Paper>
          </Flex>
        </Flex>
      )}

      <ViewFileModal
        disclosureHost={viewDisclosure}
        title="Preview Document"
        w="60vw"
        h="60vh"
      />
      <SendToPatientModal disclosureHost={addToPatientChartDisclosure} />
      <AssignAndCreateTaskModal
        disclosureHost={assignAndCreateTaskDisclosure}
      />
    </Flex>
  );
};

export default FaxHeader;
